import UploadFileFilledErrorIcon from 'ext-ui/svg/icon/UploadFileFilledErrorIcon'
import UploadFileFilledIcon from 'ext-ui/svg/icon/UploadFileFilledIcon'
import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

import style from './AttachmentListRows.style'
import AttachmentRow from './AttachmentRow'
import { statusForAttachment, statusForRow } from './statusForRow'
import WideDropzone from './WideDropzone'

interface AttachmentListRowsProps {
  attachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails?: Record<string, string>
  failedUploadThumbnails?: Record<string, string>
  recentlyUploadedAttachmentIds?: Set<string>
  uploadAttachments?: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment?: (attachmentId: string) => void
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  integrationName?: string
  integrationLogoSrc?: string
  erpStatusByAttachmentId?: Record<string, ConnectedPlatformStatus>
  showVerificationStatus?: boolean
}

const uploadingRowStyle = css`
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 16px;

  .uploading-text {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
    font-family: Rubik, sans-serif;

    .filename {
      font-size: 14px;
      line-height: 18px;
      color: #1e2c4c;
    }

    .meta {
      font-size: 12px;
      line-height: 16px;
      color: #697080;
    }

    .progress {
      width: 200px;
      height: 4px;
      border-radius: 4px;
      overflow: hidden;
      background: rgba(218, 219, 224, 0.6);
      margin-top: 6px;
    }

    .progress-fill {
      height: 100%;
      background: #4489f9;
      width: 40%;
      border-radius: 4px;
      animation: attachment-row-indeterminate 1.2s ease-in-out infinite
        alternate;
      animation-delay: -0.6s;
    }
  }
`

function UploadingRow(): React.ReactElement {
  const translate = useTranslate()
  const loadingLabel = translate('Loading...')
  return (
    <div css={uploadingRowStyle} data-testid="attachmentRow-uploading">
      <div className="thumb-placeholder">
        <UploadFileFilledIcon width={24} height={24} />
      </div>
      <div className="uploading-text">
        <span className="filename">{translate('Receipt')}</span>
        <span className="meta">{loadingLabel}</span>
        <div className="progress" role="progressbar" aria-label={loadingLabel}>
          <div className="progress-fill" />
        </div>
      </div>
    </div>
  )
}

const failedRowStyle = css`
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 16px;

  .failed-text {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
    font-family: Rubik, sans-serif;

    .filename {
      font-size: 14px;
      line-height: 18px;
      color: #d5404a;
    }

    .meta {
      font-size: 12px;
      line-height: 16px;
      color: #697080;
    }

    .progress {
      width: 200px;
      height: 4px;
      border-radius: 4px;
      overflow: hidden;
      background: #d5404a;
      margin-top: 6px;
    }
  }
`

function FailedUploadRow(): React.ReactElement {
  const translate = useTranslate()
  return (
    <div css={failedRowStyle} data-testid="attachmentRow-failed">
      <div className="thumb-placeholder">
        <UploadFileFilledErrorIcon width={24} height={24} />
      </div>
      <div className="failed-text">
        <span className="filename">{translate('Receipt')}</span>
        <span className="meta">{translate('Failed to upload')}</span>
        <div
          className="progress"
          role="progressbar"
          aria-label={translate('Failed to upload')}
        />
      </div>
    </div>
  )
}

export default function AttachmentListRows(
  props: AttachmentListRowsProps,
): React.ReactElement {
  const {
    attachments,
    uploadingAttachmentThumbnails,
    failedUploadThumbnails,
    recentlyUploadedAttachmentIds,
    uploadAttachments,
    deleteAttachment,
    setAttachmentError,
    setErrors,
    integrationName,
    integrationLogoSrc,
    erpStatusByAttachmentId,
    showVerificationStatus = true,
  } = props

  const uploadingEntries = Object.entries(uploadingAttachmentThumbnails ?? {})

  return (
    <div
      css={style}
      className="AttachmentListRows"
      data-testid="attachmentListRows"
    >
      {uploadAttachments && (
        <WideDropzone
          uploadAttachments={uploadAttachments}
          setAttachmentError={setAttachmentError}
          setErrors={setErrors}
          attachmentsLength={attachments.length}
        />
      )}
      {uploadingEntries.map(([uploadId]) => (
        <UploadingRow key={uploadId} />
      ))}
      {Object.keys(failedUploadThumbnails ?? {}).map((uploadId) => (
        <FailedUploadRow key={uploadId} />
      ))}
      {[...attachments]
        .sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return dateB - dateA
        })
        .map((attachment) => {
          const erpStatus = erpStatusByAttachmentId?.[attachment.id]
          const rowStatus = statusForAttachment(attachment, {
            erpStatus,
            erpIntegrationName: integrationName,
          })
          const awaitingVerification =
            showVerificationStatus &&
            rowStatus.statusLabel === 'Complete' &&
            !attachment.verification &&
            !erpStatus &&
            recentlyUploadedAttachmentIds?.has(attachment.id)
          const finalStatus = awaitingVerification
            ? statusForRow({
                uploadState: 'verifying',
                uploadType: attachment.uploadType,
              })
            : rowStatus
          return (
            <AttachmentRow
              key={attachment.id}
              attachment={attachment}
              status={finalStatus}
              integrationName={integrationName}
              integrationLogoSrc={integrationLogoSrc}
              deleteAttachment={deleteAttachment}
            />
          )
        })}
    </div>
  )
}
